/**
 * ingredientUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared utilities for parsing, sorting, standardizing, and summing
 * ingredient strings across Recipe.jsx and WeeklyPlanner.jsx
 *
 * ingredients.json is now the single source of truth:
 *   Array of { name, category, aliases[] }
 *   — used here for name standardization
 *   — used in AddRecipe.jsx for autocomplete
 * ─────────────────────────────────────────────────────────────────────────────
 */

import ingredientsData from '../data/ingredients.json';

// ─────────────────────────────────────────────────────────────────────────────
// BUILD LOOKUP STRUCTURES FROM ingredients.json
// ─────────────────────────────────────────────────────────────────────────────

// Flat list of { name, canonical } for every name + alias, sorted longest first
// so multi-word names match before their substrings.
const NAME_LOOKUP = ingredientsData
    .flatMap(entry => [
        { match: entry.name.toLowerCase().trim(), canonical: entry.name },
        ...(entry.aliases || []).map(alias => ({
            match: alias.toLowerCase().trim(),
            canonical: entry.name,
        })),
    ])
    .sort((a, b) => b.match.length - a.match.length);

// ─────────────────────────────────────────────────────────────────────────────
// UNIT NORMALIZATION
// ─────────────────────────────────────────────────────────────────────────────

const UNIT_ALIASES = {
    // Volume — US
    'tsp': 'tsp', 'teaspoon': 'tsp', 'teaspoons': 'tsp',
    'tbsp': 'tbsp', 'tablespoon': 'tbsp', 'tablespoons': 'tbsp', 'tbs': 'tbsp',
    'fl oz': 'fl oz', 'fluid oz': 'fl oz', 'fluid ounce': 'fl oz', 'fluid ounces': 'fl oz',
    'cup': 'cup', 'cups': 'cup', 'c': 'cup',
    'pt': 'pt', 'pint': 'pt', 'pints': 'pt',
    'qt': 'qt', 'quart': 'qt', 'quarts': 'qt',
    'gal': 'gal', 'gallon': 'gal', 'gallons': 'gal',
    'ml': 'ml', 'milliliter': 'ml', 'milliliters': 'ml',
    'l': 'l', 'liter': 'l', 'liters': 'l',
    // Weight
    'oz': 'oz', 'ounce': 'oz', 'ounces': 'oz',
    'lb': 'lb', 'lbs': 'lb', 'pound': 'lb', 'pounds': 'lb',
    'g': 'g', 'gram': 'g', 'grams': 'g',
    'kg': 'kg', 'kilogram': 'kg', 'kilograms': 'kg',
    // Count-like
    'clove': 'clove', 'cloves': 'clove',
    'slice': 'slice', 'slices': 'slice',
    'piece': 'piece', 'pieces': 'piece',
    'pinch': 'pinch', 'pinches': 'pinch',
    'dash': 'dash', 'dashes': 'dash',
    'can': 'can', 'cans': 'can',
    'package': 'package', 'packages': 'package', 'pkg': 'package',
    'bunch': 'bunch', 'bunches': 'bunch',
    'stalk': 'stalk', 'stalks': 'stalk',
    'sprig': 'sprig', 'sprigs': 'sprig',
    'head': 'head', 'heads': 'head',
    'strip': 'strip', 'strips': 'strip',
    'stick': 'stick', 'sticks': 'stick',
};





// ─────────────────────────────────────────────────────────────────────────────
// FRACTION UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

export const fractionToDecimal = (str) => {
    const s = str.trim();
    if (s.includes('/')) {
        const [n, d] = s.split('/').map(Number);
        return n / d;
    }
    return parseFloat(s);
};

export const decimalToFraction = (decimal) => {
    if (decimal === 0) return '0';
    const tolerance = 0.02;
    const common = [[1,8],[1,4],[1,3],[1,2],[2,3],[3,4],[7,8]];
    const whole = Math.floor(decimal);
    const rem   = decimal - whole;
    if (rem < tolerance)     return whole === 0 ? '0' : String(whole);
    if (rem > 1 - tolerance) return String(whole + 1);
    let bestFrac = null, bestDiff = Infinity;
    for (const [n, d] of common) {
        const diff = Math.abs(rem - n / d);
        if (diff < bestDiff) { bestDiff = diff; bestFrac = [n, d]; }
    }
    if (bestDiff < tolerance && bestFrac) {
        const fs = `${bestFrac[0]}/${bestFrac[1]}`;
        return whole === 0 ? fs : `${whole} ${fs}`;
    }
    return String(parseFloat(decimal.toFixed(2)));
};

// ─────────────────────────────────────────────────────────────────────────────
// INGREDIENT STRING PARSER
// ─────────────────────────────────────────────────────────────────────────────

export const parseIngredient = (original) => {
    const s = original.trim();

    const unitKeys = Object.keys(UNIT_ALIASES).sort((a, b) => b.length - a.length);
    const unitRe   = new RegExp(
        `^(\\d+(?:\\.\\d+)?(?:\\s+\\d+\\/\\d+|\\s+and\\s+\\d+\\/\\d+|\\/\\d+)?)` +
        `(?:\\s*-\\s*(\\d+(?:\\.\\d+)?(?:\\s+\\d+\\/\\d+)?))? ` +
        `\\s*(?:(${unitKeys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}))?` +
        `\\.?\\s*(.*)$`,
        'i'
    );

    const m = s.match(unitRe);
    if (!m) return { qty: null, unit: null, rest: s };

    const qtyRaw = m[1].trim();
    let qty = 0;
    const andMatch  = qtyRaw.match(/^(\d+)\s+and\s+(\d+\/\d+)$/i);
    const mixMatch  = qtyRaw.match(/^(\d+)\s+(\d+\/\d+)$/);
    const fracMatch = qtyRaw.match(/^(\d+\/\d+)$/);
    if (andMatch)       qty = parseInt(andMatch[1])  + fractionToDecimal(andMatch[2]);
    else if (mixMatch)  qty = parseInt(mixMatch[1])  + fractionToDecimal(mixMatch[2]);
    else if (fracMatch) qty = fractionToDecimal(fracMatch[1]);
    else                qty = parseFloat(qtyRaw) || 0;

    const qtyHiRaw = m[2] ? m[2].trim() : null;
    const qtyHi    = qtyHiRaw ? parseFloat(qtyHiRaw) : null;
    const rawUnit  = m[3] ? m[3].toLowerCase().trim() : null;
    const unit     = rawUnit ? (UNIT_ALIASES[rawUnit] || null) : null;
    const rest     = m[4] ? m[4].trim() : '';

    return { qty, qtyHi, unit, rest, original };
};

// ─────────────────────────────────────────────────────────────────────────────
// STANDARDIZED NAME LOOKUP
// Uses the new flat NAME_LOOKUP array built from ingredients.json
// ─────────────────────────────────────────────────────────────────────────────

export const standardizeName = (ingredientString) => {
    const lower = ingredientString.toLowerCase();
    for (const { match, canonical } of NAME_LOOKUP) {
        const re = new RegExp(`\\b${match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (re.test(lower)) return canonical;
    }
    return null;
};

/**
 * Search ingredients by partial name or alias — used by AddRecipe autocomplete.
 * Returns up to `limit` matches, names starting with query ranked first.
 */
export const searchIngredients = (query, dbIngredients, limit = 10) => {
  if (!query || query.trim().length === 0) return [];
  if (!dbIngredients || dbIngredients.length === 0) return [];
  const q = query.toLowerCase().trim();
  const seen = new Set();
  const results = [];

  for (const entry of dbIngredients) {
    if (seen.has(entry.name)) continue;
    const nameMatch  = entry.name.toLowerCase().includes(q);
    const aliasMatch = (entry.aliases || []).some(a => a.toLowerCase().includes(q));
    if (nameMatch || aliasMatch) {
      seen.add(entry.name);
      results.push(entry);
    }
    if (results.length >= limit * 2) break;
  }

  results.sort((a, b) => {
    const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
    const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
    return aStarts - bStarts || a.name.localeCompare(b.name);
  });

  return results.slice(0, limit);
};

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORY ORDER  (store-aisle sequence for grocery list display)
// ─────────────────────────────────────────────────────────────────────────────

export const CATEGORY_ORDER = [
    'Produce',
    'Meat & Pork',
    'Poultry',
    'Seafood',
    'Dairy & Eggs',
    'Bread & Bakery',
    'Grains, Pasta & Rice',
    'Canned & Jarred',
    'Oils, Sauces & Condiments',
    'Herbs & Spices',
    'Baking & Sweets',
    'Other',
];

export const CATEGORY_EMOJI = {
    'Produce':                   '🥦',
    'Meat & Pork':               '🥩',
    'Poultry':                   '🍗',
    'Seafood':                   '🐟',
    'Dairy & Eggs':              '🥛',
    'Bread & Bakery':            '🍞',
    'Grains, Pasta & Rice':      '🌾',
    'Canned & Jarred':           '🥫',
    'Oils, Sauces & Condiments': '🧴',
    'Herbs & Spices':            '🌿',
    'Baking & Sweets':           '🧁',
    'Other':                     '🛒',
};

// Build a fast name→category lookup from the ingredients data
const CATEGORY_LOOKUP = new Map(
    ingredientsData.flatMap(entry => [
        [entry.name.toLowerCase(), entry.category],
        ...(entry.aliases || []).map(a => [a.toLowerCase(), entry.category]),
    ])
);

/**
 * Return the store-aisle category for an ingredient string.
 * Tries standardized name first, then raw string lookup, falls back to 'Other'.
 */
export const getIngredientCategory = (ingredientString) => {
    const canonical = standardizeName(ingredientString);
    if (canonical) {
        const cat = CATEGORY_LOOKUP.get(canonical.toLowerCase());
        if (cat) return cat;
    }
    // Fallback: scan raw string against category lookup
    const lower = ingredientString.toLowerCase();
    for (const [key, cat] of CATEGORY_LOOKUP) {
        const re = new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (re.test(lower)) return cat;
    }
    return 'Other';
};

/**
 * Group an array of { display, standardName, summed } items by store-aisle category.
 * Returns an array of { category, emoji, items[] } in CATEGORY_ORDER sequence.
 */
export const groupByCategory = (combinedItems) => {
    const map = new Map();
    for (const item of combinedItems) {
        const cat = getIngredientCategory(item.standardName || item.display);
        if (!map.has(cat)) map.set(cat, []);
        map.get(cat).push(item);
    }
    // Sort within each category alphabetically
    for (const items of map.values()) {
        items.sort((a, b) => (a.standardName || a.display).localeCompare(b.standardName || b.display));
    }
    // Return in store-aisle order, appending any unknown categories at the end
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
        if (map.has(cat)) ordered.push({ category: cat, emoji: CATEGORY_EMOJI[cat] || '🛒', items: map.get(cat) });
    }
    for (const [cat, items] of map) {
        if (!CATEGORY_ORDER.includes(cat)) ordered.push({ category: cat, emoji: '🛒', items });
    }
    return ordered;
};

/**
 * Group raw ingredient strings from a single recipe by category.
 * Returns { category, emoji, items: string[] }[]
 */
export const groupIngredientsByCategory = (ingredientStrings) => {
    const map = new Map();
    for (const str of ingredientStrings) {
        const cat = getIngredientCategory(str);
        if (!map.has(cat)) map.set(cat, []);
        map.get(cat).push(str);
    }
    for (const items of map.values()) {
        items.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
        if (map.has(cat)) ordered.push({ category: cat, emoji: CATEGORY_EMOJI[cat] || '🛒', items: map.get(cat) });
    }
    for (const [cat, items] of map) {
        if (!CATEGORY_ORDER.includes(cat)) ordered.push({ category: cat, emoji: '🛒', items });
    }
    return ordered;
};

const sortKey = (str) => {
    const parsed = parseIngredient(str);
    const std    = standardizeName(str);
    return (std || parsed.rest || str).toLowerCase().trim();
};

export const sortIngredients = (ingredients) =>
    [...ingredients].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

// ─────────────────────────────────────────────────────────────────────────────
// FORMAT QTY + UNIT
// ─────────────────────────────────────────────────────────────────────────────

const formatQtyUnit = (qty, unit) => {
    if (!qty) return '';

    const plural = qty !== 1 ? 's' : '';
    const pluralUnits = {
        tsp: `tsp`, tbsp: `tbsp`, cup: `cup${plural}`,
        pt: `pt`, qt: `qt`, gal: `gal`, oz: `oz`, lb: `lb${plural}`,
        ml: `ml`, l: `l`, g: `g`, kg: `kg`,
        'fl oz': `fl oz`, clove: `clove${plural}`, slice: `slice${plural}`,
        piece: `piece${plural}`, can: `can${plural}`, package: `package${plural}`,
        bunch: `bunch${plural === 's' ? 'es' : ''}`,
        pinch: `pinch${plural === 's' ? 'es' : ''}`,
        dash: `dash${plural === 's' ? 'es' : ''}`,
        stalk: `stalk${plural}`, sprig: `sprig${plural}`,
        head: `head${plural}`, strip: `strip${plural}`, stick: `stick${plural}`,
    };

    const unitStr = unit ? (pluralUnits[unit] || unit) : '';
    return `${decimalToFraction(qty)}${unitStr ? ' ' + unitStr : ''}`.trim();
};

// ─────────────────────────────────────────────────────────────────────────────
// COMBINE + SUM INGREDIENTS
// ─────────────────────────────────────────────────────────────────────────────

export const combineIngredients = (ingredientStrings) => {
    const groups = new Map();

    for (const original of ingredientStrings) {
        const { qty, unit, rest } = parseIngredient(original);
        const stdName = standardizeName(original);
        const key     = stdName || original.toLowerCase().trim();

        if (!groups.has(key)) {
            groups.set(key, {
                standardName: stdName,
                label:        stdName || rest || original,
                entries:      [],
                unitBuckets:  {},  // keyed by exact unit string
                noUnitQty:    0,
                noQty:        false,
            });
        }

        const g = groups.get(key);
        g.entries.push(original);

        if (qty && unit) {
            // Bucket by exact unit — no cross-unit conversion
            if (!g.unitBuckets[unit]) g.unitBuckets[unit] = 0;
            g.unitBuckets[unit] += qty;
        } else if (qty && !unit) {
            g.noUnitQty += qty;
        } else {
            g.noQty = true;
        }
    }

    const results = [];
    for (const [, g] of groups) {
        const parts = [];

        for (const [unit, total] of Object.entries(g.unitBuckets)) {
            parts.push(formatQtyUnit(total, unit));
        }

        if (g.noUnitQty > 0) parts.push(decimalToFraction(g.noUnitQty));

        if (g.noQty && parts.length === 0) {
            results.push({ display: g.label, standardName: g.standardName, summed: g.entries.length > 1 });
            continue;
        }

        const qtyStr  = parts.join(' + ');
        const nameStr = g.label.charAt(0).toUpperCase() + g.label.slice(1);
        const summed  = g.entries.length > 1;
        const display = qtyStr ? `${qtyStr} ${nameStr}` : nameStr;
        results.push({ display, standardName: g.standardName, summed });
    }

    return results.sort((a, b) =>
        (a.standardName || a.display).localeCompare(b.standardName || b.display)
    );
};